import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { Router } from '@angular/router';
import {StringUtils} from '../../utils/string-utils';
import {SureFlowUnitService} from '../../services/sure-flow-unit.service';
import {WorkingSureFlowUnit} from '../../model/working-sure-flow-unit';
import {SureFlowLoopService} from '../../services/sure-flow-loop.service';
import {WorkingSureFlowLoop} from '../../model/working-sure-flow-loop';
import {SureFlowUnitData} from '../../model/sure-flow-unit-data';
import {PersonLogin} from '../../person/person-login';
import {PersonLoginService} from '../../services/person-login-service';
import {SureFlowRatingService} from '../../services/sure-flow-rating.service';
import {SureFlowWorkingRatingInput} from '../../model/sure-flow-working-rating-input';
import {SureFlowRatingUnit} from '../../model/sure-flow-rating-unit';
import {SureFlowRatingUnitRequest} from '../../model/sure-flow-rating-unit-request';
import {SureFlowRatingCoilRequest} from '../../model/sure-flow-rating-coil-request';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {SureFlowRatingToolComponent} from '../sure-flow-rating-tool/sure-flow-rating-tool.component';

import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';

@Component({
    selector: 'app-working-sure-flow-unit',
    templateUrl: './working-sure-flow-unit.component.html',
    styleUrls: ['./working-sure-flow-unit.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, SureFlowRatingToolComponent, MatProgressSpinner]
})
export class WorkingSureFlowUnitComponent implements OnChanges, OnInit {
  private readonly router = inject(Router);
  private readonly unitService = inject(SureFlowUnitService);
  private readonly loopService = inject(SureFlowLoopService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly sureFlowRatingService = inject(SureFlowRatingService);
  readonly loopId = input.required<string>();
  readonly unitIdParam = input.required<string>({ alias: 'unitId' });
  readonly workingLoopId = signal<number>(undefined!);
  readonly unitId = signal<number>(undefined!);
  readonly loopUnits = signal<WorkingSureFlowUnit[]>(undefined!);
  readonly selectedUnit = signal<WorkingSureFlowUnit>(undefined!);
  readonly loop = signal<WorkingSureFlowLoop>(undefined!);
  readonly unitData = signal<SureFlowUnitData>(undefined!);
  readonly ratingUnit = signal<SureFlowRatingUnit>(undefined!);
  updatedUnit!: WorkingSureFlowUnit;
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  private personLogin!: PersonLogin;

  ngOnInit(): void {
    this.initialize();
    this.isBusy.set(true);
    this.personLogin = this.personLoginService.getCachedLogin()!;
    this.loopService.getWorkingLoop(this.workingLoopId()).subscribe({
      next: (loop) => {
        this.loop.set(loop);
        this.unitService.getWorkingLoopUnits(this.workingLoopId()).subscribe({
          next: (units) => {
            this.loopUnits.set(units.sort((first, second) => first.sequence - second.sequence));
            if (this.unitId() > 0) {
              this.selectedUnit.set(this.loopUnits().filter(unit => unit.id === this.unitId())[0]);
            }
            this.buildUnitData();
            this.isBusy.set(false);
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.formatErrorMessage(error));
  }

  backToLoopDetail(): void {
    this.router.navigate(['ratings', 'sureflowLoops', this.workingLoopId()]);
  }

  private buildUnitData(): void {
    if ((this.selectedUnit()?.id || 0) > 0) {
      this.unitData.set(JSON.parse(JSON.stringify(this.selectedUnit().unitData)));
      this.ratingUnit.set(JSON.parse(JSON.stringify(this.selectedUnit().unitData.sureFlowRatingUnit)));
    } else {
      this.unitData.set(new SureFlowUnitData());
      this.unitData().active = true;
      this.unitData().userTag = '';
      this.ratingUnit.set(new SureFlowRatingUnit());
      this.ratingUnit().request = new SureFlowRatingUnitRequest();
      this.ratingUnit().request.fanSpeed = 'H';
      this.ratingUnit().request.primaryCoilRequest = new SureFlowRatingCoilRequest();
      this.ratingUnit().airFilterCode = '';
    }
    this.ratingUnit().request.metric = this.loop().detail.metric;
  }

  performanceUpdate(response: WorkingSureFlowUnit): void {
    if ((this.selectedUnit()?.sequence || 0) > 0) {
      this.updatedUnit = JSON.parse(JSON.stringify(this.selectedUnit()));
      this.updatedUnit.unitData = response.unitData;
    } else {
      this.updatedUnit = response;
      if ((this.updatedUnit.sequence || 0) === 0) {
        this.updatedUnit.sequence = (this.loopUnits()?.length || 0) + 1;
      }
    }
  }

  canSave(): boolean {
    return !this.isBusy() && (this.updatedUnit?.unitData?.sureFlowRatingUnit?.response?.airflow || 0) > 0;
  }

  saveUnit(): void {
    if (this.unitId() > 0) {
      this.updateUnit();
    } else {
      this.addUnit();
    }
  }

  private updateUnit(): void {
    if (this.canSave()) {
      this.isBusy.set(true);
      const units: WorkingSureFlowUnit[] = [];
      let sequence: number = 1;
      for (const unit of this.loopUnits()) {
        if (this.updatedUnit.id === unit.id) {
          const workingUnit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(this.updatedUnit));
          workingUnit.sequence = sequence++;
          units.push(workingUnit);
        } else {
          const workingUnit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(unit));
          workingUnit.sequence = sequence++;
          units.push(workingUnit);
        }
      }
      const input: SureFlowWorkingRatingInput = new SureFlowWorkingRatingInput(this.loop(), units);
      this.sureFlowRatingService.updateAndSaveWorkingLoopAndUnits(input).subscribe({
        next: () => this.backToLoopDetail(),
        error: (error) => this.handleError(error)
      });
    }
  }

  private addUnit(): void {
    if (this.canSave()) {
      this.isBusy.set(true);
      const units: WorkingSureFlowUnit[] = [];
      let sequence: number = 1;
      for (const unit of this.loopUnits()) {
        const workingUnit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(unit));
        workingUnit.sequence = sequence++;
        units.push(workingUnit);
      }
      const workingUnit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(this.updatedUnit));
      workingUnit.sequence = sequence++;
      units.push(workingUnit);
      const input: SureFlowWorkingRatingInput = new SureFlowWorkingRatingInput(this.loop(), units);
      this.sureFlowRatingService.updateAndSaveWorkingLoopAndUnits(input).subscribe({
        next: () => this.backToLoopDetail(),
        error: (error) => this.handleError(error)
      });
    }
  }


  private initialize(): void {
    this.workingLoopId.set(isNaN(+this.loopId()) ? 0 : +this.loopId());
    this.unitId.set(isNaN(+this.unitIdParam()) ? 0 : +this.unitIdParam());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }
}
